// Pure visual sub-component for a single notification row on the Admin
// Notification Center page. Extracted from NotificationCenterPage so
// SwipeableNotificationCard can wrap it with a gesture layer without
// forking the visual.
//
// Stays a real <button> so screen readers and keyboard users can activate
// the notification with Enter / Space exactly as before.

import { Badge } from '@bbpos/ui';
import type { Notification, NotificationType } from '@/services/notification.service';
import { GLASS_INTERACTIVE, cn, toneClasses } from '@/constants/design-tokens';
import type { Tone } from '@/constants/design-tokens';

// ─── Type → Icon + Tone mapping ────────────────────────────────────────────

interface TypeMeta { tone: Tone; iconPath: string }

export const TYPE_META: Record<NotificationType, TypeMeta> = {
  LOW_STOCK: { tone: 'warning', iconPath: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
  EXPENSE_APPROVAL: { tone: 'danger', iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  APPROVAL_RESULT: { tone: 'success', iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  SYNC_CONFLICT: { tone: 'danger', iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
  ORDER_UPDATE: { tone: 'violet', iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
  SHIFT_REMINDER: { tone: 'brand', iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  TRIAL_EXPIRY: { tone: 'warning', iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  SYSTEM_ANNOUNCEMENT: { tone: 'info', iconPath: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z' },
  REPORT_READY: { tone: 'success', iconPath: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  BACKUP_COMPLETE: { tone: 'teal', iconPath: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4' },
  CREDIT_LIMIT: { tone: 'danger', iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
  APPROVAL_REQUEST: { tone: 'warning', iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  ORDER_NEW: { tone: 'violet', iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
  TABLE_STATUS: { tone: 'brand', iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
  PLATFORM_SUSPENSION: { tone: 'danger', iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
  TENANT_CANCELLED: { tone: 'danger', iconPath: 'M6 18L18 6M6 6l12 12' },
  BILLING_FAILED: { tone: 'danger', iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
  EMAIL_DELIVERY_FAILED: { tone: 'danger', iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  EMAIL_DELIVERY_RESTORED: { tone: 'success', iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  QUOTE_APPROVED: { tone: 'success', iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  QUOTE_REJECTED: { tone: 'danger', iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' },
  QUOTE_CHANGES_REQUESTED: { tone: 'violet', iconPath: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
  VENDOR_BILL_DUE_SOON: { tone: 'warning', iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  VENDOR_BILL_OVERDUE: { tone: 'danger', iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
  // L04 — a clipboard with a tick: a purchase order waiting for the owner.
  PO_APPROVAL_REQUEST: { tone: 'violet', iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface NotificationCardBodyProps {
  notification: Notification;
  onClick: () => void;
  /** Optional node slotted into the right indicator stack (e.g. lg+ "Mark as read" mini button). */
  trailingAccessory?: React.ReactNode;
}

export function NotificationCardBody({
  notification,
  onClick,
  trailingAccessory,
}: NotificationCardBodyProps) {
  const meta = TYPE_META[notification.type] ?? TYPE_META.SYSTEM_ANNOUNCEMENT;
  const tone = toneClasses(meta.tone);
  const isUnread = !notification.is_read;
  const isUrgent = notification.priority === 'URGENT';
  const isHigh = notification.priority === 'HIGH';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        GLASS_INTERACTIVE,
        'w-full text-left p-4 animate-slide-up flex items-start gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        isUnread && 'bg-brand-50/30 dark:bg-brand-900/10',
      )}
    >
      {/* Icon */}
      <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', tone.icon)}>
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={meta.iconPath} />
        </svg>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm',
          isUnread ? 'font-bold text-neutral-900 dark:text-neutral-100' : 'font-medium text-neutral-700 dark:text-neutral-300',
        )}>
          {notification.title}
        </p>
        {/* The whole message (T24): a click on a linked notification opens the
            record, so this list is where its full text is read. */}
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 whitespace-pre-line break-words">
          {notification.message}
        </p>
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1 tabular-nums">
          {relativeTime(notification.created_at)}
        </p>
      </div>

      {/* Right indicators */}
      <div className="flex items-center gap-2 shrink-0">
        {isUrgent && (
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500" />
          </span>
        )}
        {isHigh && !isUrgent && (
          <Badge variant="warning" size="sm">High</Badge>
        )}
        {isUnread && !isUrgent && (
          <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
        )}
        {trailingAccessory}
      </div>
    </button>
  );
}
